import { ReactNode, useEffect, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import CloseIcon from '@/components/icons/close';
import DeleteIcon from '@/components/icons/delete';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

function TypeDeleteConfirmationDialog({
  open,
  onOpenChange,
  callback,
  loading,
  title,
  description,
  note,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: () => void;
  loading: boolean;
  title: ReactNode;
  description: ReactNode;
  note?: ReactNode;
}) {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === 'DELETE';

  // Reset input when dialog closes
  useEffect(() => {
    if (!open) setConfirmText('');
  }, [open]);

  function handleSubmit() {
    if (isConfirmed) callback();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-[10px]">
        {/* Visually hidden for a11y */}
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>

        <DialogClose className="focus:ring-default-blue data-[state=open]:bg-default-blue/10 data-[state=open]:text-default-text ml-auto size-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
          <CloseIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <h1 className="text-center text-xl font-medium">{title}</h1>

        <DialogDescription className="text-center">{description}</DialogDescription>

        {note && <p className="text-center text-sm">{note}</p>}

        <p className="text-center">
          Type <strong>DELETE</strong> to confirm
        </p>

        <Input placeholder="Type DELETE" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />

        <div className="mt-4 flex justify-center gap-2">
          <Button variant="destructive" icon={<DeleteIcon className="text-white" />} onClick={handleSubmit} loading={loading} disabled={!isConfirmed}>
            Delete
          </Button>
          <Button variant="grey" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { TypeDeleteConfirmationDialog };
